<template>
<section>
    <loading v-if="loading"></loading>
    <template v-if="!loading">
        <div class="profile-media-buttons" v-if="owner">
            <a href="#" class="btn btn-default profile-media-buttons-btn" @click.prevent="emailVerifyCheck('redirect', pages.myInvites)">{{ t.manage_invites }}</a>
            <button v-if="showWebcamButton" class="btn btn-default profile-media-buttons-btn" @click="emailVerifyCheck('toggle', 'showWebcam')">
                <i class="fa fa-camera fa-m3"></i> {{ t.webcam }}
            </button>
            <media-upload v-if="!notEmailVerified" :album="currentAlbum.id" :private-album="privateAlbum" :show-cancel="true" :set="currentAlbum.typeId === 1 ? 7 : null"
                :is-misc="currentAlbum.typeId === 2" @media-upload-complete="uploadComplete" :collapsed="true"
                ref="mediaUpload" :buttonText="t.add_media"></media-upload>
            <div v-else class="add-media btn btn-default btn-large" @click="emailVerifyCheck">
                <i class="fa fa-m3 fa-upload"></i>{{ t.add_media }}
            </div>
        </div>
        <h1>{{ t.media }} - <i v-if="currentAlbum.private" class="fa fa-lock" :title="t.private_album"></i> {{ currentAlbum.name }}</h1>
        <div id="profile-photos-webcam" v-if="showWebcam">
            <div class="col-md-6 col-md-offset-3">
                <webcam-photo @upload="addPhotoToUploader"></webcam-photo>
            </div>
        </div>
        <div v-if="currentAlbum.typeId === 2 && currentAlbum.count" class="alert alert-info text-center">{{ t.misc_album_notice }}</div>
        <profile-media-media :current-album="currentAlbum" :owner="owner" :albums="albums" :pagination="pagination" :edit-page="editPage"></profile-media-media>
        <profile-media-albums :albums="albums" @change-album="changeAlbum" @email-verify="showEmailVerify = true"
            :owner="owner"></profile-media-albums>
    </template>
    <modal v-model="showEmailVerify" :title="t.email_verify_title" :hide-footer="true">
        <email-verify></email-verify>
    </modal>
</section>
</template>

<script>
import ajax from 'Site/js/util/ajax.fetch';
import analytics from 'Site/js/util/analytics';
import EmailVerifyModal from 'User/js/mixin/email.verify.modal';
import Impressions from 'Site/js/util/impressions';
import Loading from 'Site/component/Loading.vue';
import MediaUpload from 'Media/component/MediaUpload.vue';
import Modal from 'Site/component/Modal.vue';
import WebcamPhoto from 'Media/component/WebcamPhoto.vue';
import { mapState } from 'vuex';
import ProfileMediaAlbums from './ProfileMediaAlbums.vue';
import ProfileMediaMedia from './ProfileMediaMedia.vue';

export default {
    name: 'ProfileMedia',
    mixins: [EmailVerifyModal],
    components: {
        Loading,
        MediaUpload,
        Modal,
        ProfileMediaAlbums,
        ProfileMediaMedia,
        WebcamPhoto
    },
    computed: {
        ...mapState({
            notEmailVerified: state => state.config.data.verifyId !== null,
            owner: state => state.config.data.owner,
            pages: state => state.config.pages,
            routerConfig: state => state.config.data.router,
            t: state => Object.assign(state.t.profile_media, state.t.assets),
        }),
        privateAlbum() {
            const albums = this.albums.filter(album => album.type === 'private');
            if (albums.length) {
                return albums[0].id;
            }
            return null;
        }
    },
    data() {
        return {
            albums: [],
            currentAlbum: {},
            editPage: '',
            impressions: null,
            loading: true,
            pagination: {},
            mediaTop: 0,
            showWebcamButton: false,
            showWebcam: false
        };
    },
    methods: {
        addPhotoToUploader(file) {
            this.showWebcam = false;
            this.$refs.mediaUpload.manuallyAddFile(file);
        },
        changeAlbum(album) {
            if (album.allowed) {
                this.$router.push({ path: `${this.routerConfig.photos}/${this.routerConfig.parts.album}/${album.id}` });
                document.documentElement.scrollTop = this.mediaTop;

                if (!this.owner) {
                    this.impressions.getImpressions();
                }
                this.currentAlbum = album;
            } else {
                this.$store.dispatch('notify', {
                    type: 'error',
                    msg: this.t.error_private_must_be_invited
                });
            }
        },
        emailVerifyCheck(action = '', target = null) {
            // if not email verified show modal else resume input action
            if (this.notEmailVerified) {
                this.showEmailVerify = true;
                return;
            }
            switch (action) {
                case 'redirect':
                    window.location.href = target;
                    break;
                case 'toggle':
                    this[target] = !this[target];
                    break;
                default:
                    break;
            }
        },
        uploadComplete(file) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                this.currentAlbum.collections.push({
                    media: {
                        'large-thumbnail': {
                            url: reader.result
                        }
                    }
                });
            }, false);
            reader.readAsDataURL(file);
        }
    },
    mounted() {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            this.showWebcamButton = true;
        }
        if (!this.owner) {
            this.impressions = new Impressions('.media-item', 'Profile');
        }

        if (this.$route.params.album) {
            const album = this.albums.filter(checkAlbum => checkAlbum.id === parseInt(this.$route.params.album, 10));
            if (album.length) {
                this.changeAlbum(album[0]);
            }
        }
    },
    created() {
        const setData = (settings) => {
            settings = JSON.parse(settings);
            this.albums = settings.data.albums;
            this.pagination = {
                maxNoPagination: settings.data.maxNoPagination,
                showPerPage: settings.data.showPerPage
            };
            this.currentAlbum = this.albums[settings.data.publicAlbumKey];
            this.editPage = settings.pages.edit;
            this.loading = false;

            this.$nextTick(() => {
                this.mediaTop = document.querySelector('.profile-media').getBoundingClientRect().top - 100;
            });
        };
        if (document.querySelector('#media-json')) {
            const settings = document.querySelector('#media-json').textContent;
            if (settings) {
                setData(settings);
            }
        } else {
            ajax.get(window.location.href).then((data) => {
                setData(data);
            });
        }
    },
    props: {
        showUpload: {
            type: Boolean,
            default: false
        }
    }
};

</script>

<style lang="scss">
@import "~Site/scss/variables";

.profile-media-buttons {
    .media-upload {
        width: 100%;
    }
    .profile-media-buttons-btn, .media-upload-button, .dz-message .btn, .add-media {
        float: right;
        width: auto;
    }
    .profile-media-buttons-btn, .media-upload, .dz-message .btn {
        margin: 0 0 5px 5px;
    }
}

@media (min-width: $screen-md-min) {
    .media-upload {
        padding-left: 10%;
        padding-right: 10%;
    }
    .media-upload-button {
        padding: 0;
    }
}

@media (max-width: $screen-xs-max) {
    .profile-media-buttons {
        margin-bottom: 10px;
        float: left !important;
        width:100%;
        .profile-media-buttons-btn {
            padding-left: 11px;
            padding-right: 11px;
        }
    }
}
</style>

<style lang="scss" scoped>
.profile-body h1 {
    max-width: 850px;
}
</style>
